import "./styles.css";
import { hasEd25519Support } from "./crypto";
import { renderCard } from "./screens/card";
import { renderChallengeAnswer } from "./screens/challenge";
import { type EnrolReason, renderEnrol } from "./screens/enrol";
import { renderErrandCreate } from "./screens/errand-create";
import { renderErrandReceive } from "./screens/errand-receive";
import { renderHistory } from "./screens/history";
import { renderInstall } from "./screens/install";
import { renderScan } from "./screens/scan";
import { ICON } from "./icons";
import { correctedNow, loadCredential, loadCredentialState } from "./storage";
import { credentialPhase, expiresAtOf, logout, startSync, type SyncReport } from "./sync";
import * as nav from "./nav";
import type { View } from "./nav";
import { isInstalled } from "./ui";
import { watchForUpdates } from "./update";

const root = document.querySelector<HTMLElement>("#app")!;
const bar = document.querySelector<HTMLElement>("#bar")!;
let teardown: (() => void) | null = null;
// The screen on display, or null while none of the navigable ones is: the
// install gate, enrolment, a logout under way. A history move then has nothing
// to redraw, and a back gesture on the enrolment form must not draw a card
// for a credential that is gone.
let view: View | null = null;
// The errand chosen on the card when the holder went to answer a challenge.
let challengeErrand: Uint8Array | undefined;
// Stops the sync, and null while none is running: before enrolment, and again
// after a logout.
let stopSync: (() => Promise<void>) | null = null;
// Which `show` is the latest. Screens render asynchronously, and a holder who
// taps back twice quickly must end on the second screen, not on whichever of
// the two finished drawing last.
let shown = 0;

const BAR_TITLE: Record<Exclude<View, "card">, string> = {
  "scan": "Verificar",
  "history": "Historial",
  "errand-create": "Crear recado",
  "errand-receive": "Recibir recado",
  "challenge": "Responder a un desafío",
};

/** Draws `next`. Where the holder is lives in `nav.ts`; this only draws it. */
async function show(next: View): Promise<void> {
  const mine = ++shown;
  view = next;
  teardown?.();
  teardown = null;
  window.scrollTo(0, 0);
  // Every screen but the card has the way back at the top, in one place, so
  // no screen has to draw its own.
  bar.hidden = next === "card";
  bar.querySelector("#bar-title")!.textContent = next === "card" ? "" : BAR_TITLE[next];
  drawArrows();
  const done = nav.home;
  const stop =
    next === "card"
      ? await renderCard(root, {
        createErrand: () => nav.go("errand-create"),
        receiveErrand: () => nav.go("errand-receive"),
        answerChallenge: (errandBytes) => {
          challengeErrand = errandBytes;
          nav.go("challenge");
        },
        logout: () => void logOut(),
        refresh: () => void show("card"),
      })
      : next === "scan"
        ? await renderScan(root)
        : next === "history"
          ? await renderHistory(root)
          : next === "errand-create"
            ? await renderErrandCreate(root, done)
            : next === "errand-receive"
              ? await renderErrandReceive(root, done)
              : await renderChallengeAnswer(root, done, challengeErrand);
  // Superseded while it drew: its camera or timers stop now rather than run
  // behind the screen that replaced it.
  if (mine !== shown) stop();
  else teardown = stop;
}

/** The bar's two arrows, each shown only when there is somewhere to go. */
function drawArrows(): void {
  bar.querySelector<HTMLButtonElement>("#bar-back")!.hidden = !nav.canGoBack();
  bar.querySelector<HTMLButtonElement>("#bar-forward")!.hidden = !nav.canGoForward();
}

// Back, forward, a tap on a `data-view`: every move ends here. A move inside
// the card (a sheet opening, the code closing) is the card's own business.
nav.onPlace((place) => {
  if (view === null) return;
  if (place.view !== view) void show(place.view);
  else drawArrows();
});

function showEnrol(reason?: EnrolReason): void {
  teardown?.();
  teardown = null;
  view = null;
  bar.hidden = true;
  renderEnrol(root, () => void boot(), reason);
}

/**
 * The card's «Cerrar sesión», once the holder confirmed. The card's timers
 * and the sync stop before anything is wiped: either one still running would
 * read a store that is being emptied, and a sync in flight would write a
 * status list or a renewed credential back into it afterwards.
 */
async function logOut(): Promise<void> {
  teardown?.();
  teardown = null;
  view = null;
  bar.hidden = true;
  root.innerHTML = `<p class="loading">Cerrando sesión…</p>`;
  await stopSync?.();
  stopSync = null;
  try {
    showEnrol({ loggedOut: await logout() });
  } catch {
    // The store refused to be cleared. Nothing was wiped, so say that rather
    // than show a form that claims it was.
    root.innerHTML = `<div class="problem"><div class="badge">${ICON.cross}</div>
      <h1>No se pudo cerrar la sesión</h1>
      <p>Este dispositivo no dejó borrar tu identidad. Cierra la aplicación,
      ábrela de nuevo e inténtalo otra vez.</p></div>`;
  }
}

/**
 * A renewal redraws the card so the days left are the new ones; a death goes
 * back through `boot`, which sends the holder to enrolment. A verifier in the
 * middle of a scan is left alone for a renewal: the camera is theirs.
 */
function onSyncReport(report: SyncReport): void {
  if (report.renewal === "dead") void boot();
  else if (report.renewal === "renewed" && view === "card") void show("card");
}

/**
 * The install gate, and the whole application behind it.
 *
 * It is checked here rather than only at the end of the enrolment form because
 * enrolment is not what the gate protects. Storage is: an uninstalled Safari
 * drops IndexedDB after seven idle days and takes the device key with it, and
 * a gate that fires once lets a reload of the same tab walk straight past it
 * into a working card. The spec says the alta is not finished until the
 * application is installed, so an uninstalled document is not finished either,
 * however many times it is reloaded.
 *
 * It also settles which storage the credential is minted into. A home-screen
 * web app on iOS does not reliably share its container with the Safari tab it
 * was added from, so enrolling in the tab and installing afterwards can mean
 * enrolling twice.
 */
function showInstaller(): void {
  teardown?.();
  view = null;
  bar.hidden = true;
  teardown = renderInstall(root);
}

async function boot(): Promise<void> {
  if (!(await hasEd25519Support())) {
    root.innerHTML = `<div class="problem"><div class="badge">${ICON.cross}</div>
      <h1>Navegador no compatible</h1>
      <p>Esta aplicación necesita Chrome 137, Firefox 129 o Safari 17 en
      adelante. Actualiza el navegador o el sistema y vuelve a entrar.</p></div>`;
    return;
  }
  // Before the install gate, not after: Chrome will not offer to install a
  // page that has no registered service worker, so gating first would make the
  // gate impossible to get past. The catch is here because `/sw.js` is a build
  // artefact and the dev server has none, and a service worker buys offline
  // asset caching, not verification — a rejection must not take the app down.
  if ("serviceWorker" in navigator) {
    // `watchForUpdates` is what moves the holder onto a new release: it looks
    // for one on launch, on resume and every quarter of an hour, and blocks
    // the application until the new release has taken over and reloaded it.
    try {
      watchForUpdates(await navigator.serviceWorker.register("/sw.js", { type: "module" }));
    } catch {
      /* no offline shell this run; everything below still works */
    }
  }
  if (!isInstalled()) {
    showInstaller();
    return;
  }
  const credential = await loadCredential();
  if (credential === null) {
    showEnrol();
    return;
  }
  // Dead, or expired past the issuer's grace period: no sync can bring this
  // credential back, so the holder goes straight to the form.
  const state = await loadCredentialState();
  const { now } = await correctedNow();
  if (credentialPhase(expiresAtOf(credential), now, state).phase === "re-enrol") {
    showEnrol({ dead: state?.detail ?? "expired-too-long" });
    return;
  }
  // Started once there is a credential and not before: enrolment fetches its
  // own trust store and clock, and starting here means the first sync, with
  // its status list, runs the moment enrolment finishes.
  stopSync ??= startSync(onSyncReport);
  await show(nav.startHere().view);
}

bar.innerHTML = `<button id="bar-back" class="icon" aria-label="Atrás">${ICON.back}</button>
  <span id="bar-title" class="label"></span>
  <button id="bar-forward" class="icon" aria-label="Adelante">${ICON.forward}</button>`;
bar.querySelector("#bar-back")!.addEventListener("click", nav.back);
bar.querySelector("#bar-forward")!.addEventListener("click", nav.forward);

// Wired once, at module scope, and on the document: the card's buttons and
// the menu's lead somewhere by `data-view`, and the card is redrawn too often
// to hold listeners of its own for that. Inside `boot` this would stack a
// second listener each time enrolment finishes and calls it.
document.addEventListener("click", (event) => {
  const target = (event.target as Element).closest<HTMLElement>("[data-view]");
  if (target) nav.go(target.dataset.view as View);
});

void boot();
